import { useCallback, useEffect, useMemo, useState } from 'react';
import { Printer } from 'lucide-react';
import {
  describeError,
  describeFieldError,
  getTenantPrinting,
  setTenantAgentChannel,
  type AgentUpdateChannel,
  type TenantAgentChannel,
  type TenantPrintingAgent,
  type TenantPrintingResponse,
} from '@/lib/api';
import {
  channelLabel,
  eventLabel,
  formatSince,
  formatWhen,
  jobErrorLabel,
  operatorLabel,
  printerStateLabel,
  revokeReasonLabel,
  ticketLabel,
  transportLabel,
  updateFailureLabel,
  updateLevelLabel,
  updateModeLabel,
  updatePhaseLabel,
  updateStatusLabel,
  widthLabel,
} from '@/lib/printing';
import { useAuthStore } from '@/stores/auth';
import { Badge, type BadgeTone } from './ui';
import { CardTable, THead, TBody, Tr, Th, Td } from './CardTable';
import { SimpleConfirmDialog } from './SimpleConfirmDialog';

/**
 * Seção "Impressão" do detalhe do tenant (change production-printing, §15.2).
 *
 * Existe para o suporte responder "não está imprimindo" sem ver pedido: a
 * situação das flags, se a loja pausou ou desativou e desde quando, o
 * computador com versão e último contato, as impressoras com o último erro
 * genérico, o mapa de cupons e os eventos do computador. A API não devolve
 * nada do pedido — no máximo o id do trabalho de um evento, que esta seção nem
 * mostra.
 *
 * A change print-agent acrescenta o canal de atualização do agente da loja e,
 * por computador, o protocolo aceito, o nível de atualização e o último pedido
 * de atualização com o desfecho: o suporte vê a atualização que falhou sem ter
 * ação de atualizar.
 *
 * Só leitura, de propósito: pausar, retomar, desativar e reativar são da loja,
 * na aba Impressão do painel, e pedir a atualização é do dono. A única escrita
 * é o canal do agente, e só para `Admin`. O texto de cada situação diz ao
 * operador o que orientar ao dono.
 */
export function TenantPrintingSection({ tenantId, tenantActive }: { tenantId: string; tenantActive: boolean }) {
  const [data, setData] = useState<TenantPrintingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const changeChannel = useCallback((updated: TenantAgentChannel) => {
    setData((prev) => prev && { ...prev, agentChannel: updated });
  }, []);

  useEffect(() => {
    // Tenant desativado: a rota responde 422, como pedidos e clientes. Nem pede.
    if (!tenantActive) return;
    let cancelled = false;
    getTenantPrinting(tenantId)
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [tenantId, tenantActive]);

  return (
    <section className="card" aria-labelledby="tenant-printing-title">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Printer size={18} />
        <div className="card-title" id="tenant-printing-title">
          Impressão
        </div>
        {data && <SituationBadges data={data} />}
      </div>

      {!tenantActive ? (
        <p className="muted" style={{ margin: 0 }}>
          Loja desativada: a impressão não é consultada enquanto ela estiver inativa.
        </p>
      ) : error ? (
        <div className="error-banner" role="alert">
          {error}
        </div>
      ) : !data ? (
        <p className="muted" style={{ margin: 0 }}>
          Carregando…
        </p>
      ) : (
        <PrintingDetails data={data} tenantId={tenantId} onChannelChanged={changeChannel} />
      )}
    </section>
  );
}

function situation(data: TenantPrintingResponse): { label: string; tone: BadgeTone } {
  if (data.flags.availability === 'outside_pilot') return { label: 'Fora do piloto', tone: 'neutral' };
  if (data.flags.availability === 'platform_disabled') return { label: 'Desligada pela equipe do Vapt Menu', tone: 'warning' };
  switch (data.setup.activation) {
    case 'active':
      return data.setup.paused ? { label: 'Pausada', tone: 'warning' } : { label: 'Ativa', tone: 'success' };
    case 'deactivated':
      return { label: 'Desativada pela loja', tone: 'neutral' };
    case 'never_activated':
      return { label: 'Nunca ativada', tone: 'neutral' };
  }
}

function SituationBadges({ data }: { data: TenantPrintingResponse }) {
  const main = situation(data);
  return (
    <>
      <Badge tone={main.tone} dot>
        {main.label}
      </Badge>
      {data.accessBlocked && <Badge tone="error">Sem acesso</Badge>}
    </>
  );
}

function PrintingDetails({
  data,
  tenantId,
  onChannelChanged,
}: {
  data: TenantPrintingResponse;
  tenantId: string;
  onChannelChanged: (updated: TenantAgentChannel) => void;
}) {
  const { flags, setup } = data;
  const hasHistory =
    setup.activation !== 'never_activated' || data.agents.length > 0 || data.printers.length > 0;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <SituationNote data={data} />

      {(flags.availability !== 'outside_pilot' || hasHistory) && (
        <>
          <div className="detail-grid">
            <div className="k">Flags</div>
            <div>
              <span className="mono">panel.printing</span> {flags.pilot ? 'ligada' : 'desligada'} ·{' '}
              <span className="mono">printing-enabled</span> {flags.platformEnabled ? 'ligada' : 'desligada'}
            </div>
            <div className="k">Canal do agente</div>
            <AgentChannelValue tenantId={tenantId} channel={data.agentChannel} onChanged={onChannelChanged} />
            <div className="k">Última ativação</div>
            <div>{formatWhen(setup.activatedAt)}</div>
            <div className="k">Pausa</div>
            <div>
              {setup.paused ? `Pausada desde ${formatWhen(setup.pausedAt)}` : 'Não pausada'}
              {setup.resumedAt && (
                <div className="muted" style={{ fontSize: 13 }}>
                  Última retomada: {formatWhen(setup.resumedAt)}
                </div>
              )}
            </div>
            <div className="k">Desativação pela loja</div>
            <div>{setup.deactivatedAt ? formatWhen(setup.deactivatedAt) : 'Nunca desativou'}</div>
            <div className="k">Pontos de produção</div>
            <div>
              {data.stations.count}
              {data.stations.withoutPrinter > 0 &&
                ` (${data.stations.withoutPrinter} sem impressora)`}
            </div>
          </div>

          <AgentsTable data={data} />
          <PrintersTable data={data} />
          <TicketsList data={data} />
          <EventsTable data={data} />
        </>
      )}

      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Somente leitura, exceto o canal do agente, que só administradores trocam. Pausar, retomar,
        desativar e reativar a impressão são ações da loja, na aba Impressão do painel, e pedir a
        atualização do computador é do dono ou do gerente, também pelo painel.
      </p>
    </div>
  );
}

/**
 * O canal de atualização do agente da loja, com quem trocou e quando (change print-agent).
 *
 * Só `Admin` troca, e sempre por uma confirmação que explica o efeito: a loja passa a
 * receber o instalador e as recomendações do canal novo, o pedido de atualização já aberto
 * termina no canal em que foi confirmado, e nenhum computador volta de versão. A API audita a
 * troca (`platform.print_agent_channel_changed`).
 */
function AgentChannelValue({
  tenantId,
  channel,
  onChanged,
}: {
  tenantId: string;
  channel: TenantAgentChannel;
  onChanged: (updated: TenantAgentChannel) => void;
}) {
  const isAdmin = useAuthStore((s) => s.operator?.role) === 'Admin';
  const currentOperatorId = useAuthStore((s) => s.operator?.id ?? null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const target: AgentUpdateChannel = channel.channel === 'pilot' ? 'stable' : 'pilot';
  const targetName = channelLabel(target).toLowerCase();

  async function applyChange() {
    setSaving(true);
    setError(null);
    try {
      const updated = await setTenantAgentChannel(tenantId, target);
      onChanged({
        channel: updated.channel,
        updatedAt: updated.updatedAt,
        updatedByOperatorId: updated.updatedByOperatorId,
      });
      setConfirming(false);
    } catch (err) {
      setError(describeFieldError(err));
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="agent-channel">
      <strong>{channelLabel(channel.channel)}</strong>
      <span className="muted">
        {' · '}
        {channel.updatedAt
          ? `alterado por ${operatorLabel(channel.updatedByOperatorId, currentOperatorId)} em ${formatWhen(channel.updatedAt)}`
          : 'padrão, nunca alterado'}
      </span>
      {isAdmin && (
        <div style={{ marginTop: 6 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirming(true)}>
            {target === 'pilot' ? 'Colocar no piloto' : 'Voltar para o estável'}
          </button>
        </div>
      )}
      {error && (
        <div className="error-banner" role="alert" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}

      {confirming && (
        <SimpleConfirmDialog
          title={target === 'pilot' ? 'Colocar a loja no piloto?' : 'Voltar a loja para o estável?'}
          confirmLabel={target === 'pilot' ? 'Colocar no piloto' : 'Voltar para o estável'}
          submitting={saving}
          onCancel={() => setConfirming(false)}
          onConfirm={applyChange}
          body={
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
              <li>
                A loja passa a receber o instalador e as recomendações de versão do {targetName}
                {target === 'pilot'
                  ? ', que recebe primeiro cada versão publicada, antes de ela chegar ao estável.'
                  : ', que só recebe as versões promovidas do piloto.'}
              </li>
              <li>
                Um pedido de atualização já aberto termina no canal em que foi confirmado, com a
                mesma versão-alvo.
              </li>
              <li>
                Nenhum computador volta de versão: acima da recomendada do {targetName}, ele fica
                sem aviso até o canal alcançá-lo.
              </li>
              <li>A troca fica registrada na auditoria da plataforma.</li>
            </ul>
          }
        />
      )}
    </div>
  );
}

/** O que está acontecendo e o que orientar, em uma frase para cada situação. */
function SituationNote({ data }: { data: TenantPrintingResponse }) {
  const { flags, setup } = data;

  if (flags.availability === 'outside_pilot') {
    return (
      <div className="info-banner" role="status">
        A flag <strong>panel.printing</strong> está desligada para esta loja: ela está fora do piloto,
        a impressão não existe para ela e a aba Impressão não aparece no painel.
      </div>
    );
  }

  if (flags.availability === 'platform_disabled') {
    return (
      <div className="warning-banner" role="status">
        A flag global <strong>printing-enabled</strong> está desligada: a impressão está desativada
        temporariamente pela equipe do Vapt Menu. Os computadores ficam suspensos, a fila foi cancelada, e o aceite
        automático das lojas com a impressão ativa volta ao manual. A loja não precisa fazer nada.
      </div>
    );
  }

  if (data.accessBlocked) {
    return (
      <div className="warning-banner" role="status">
        A loja está sem acesso (assinatura): a impressão fica suspensa até o acesso voltar, e o
        computador volta sozinho, sem parear de novo.
      </div>
    );
  }

  if (setup.activation === 'active' && setup.paused) {
    return (
      <div className="warning-banner" role="status">
        A impressão está <strong>pausada desde {formatWhen(setup.pausedAt)}</strong>: nenhum cupom
        sai, e os pedidos aceitos ficam "Não impresso". Oriente o dono a retomar pela chave "Pausar
        impressão", na aba Impressão.
      </div>
    );
  }

  if (setup.activation === 'deactivated') {
    return (
      <div className="info-banner" role="status">
        A loja <strong>desativou a impressão em {formatWhen(setup.deactivatedAt)}</strong>. A
        configuração ficou guardada e os computadores foram revogados; para voltar, o dono reativa
        pelo assistente da aba Impressão, pareando um computador de novo.
      </div>
    );
  }

  if (setup.activation === 'never_activated') {
    return (
      <div className="info-banner" role="status">
        A loja está no piloto, mas ainda não ativou a impressão pelo assistente da aba Impressão.
      </div>
    );
  }

  return null;
}

function AgentsTable({ data }: { data: TenantPrintingResponse }) {
  if (data.agents.length === 0) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        Nenhum computador pareado.
      </p>
    );
  }

  return (
    <div>
      <div className="card-subtitle" style={{ marginBottom: 6 }}>
        Computadores
      </div>
      <CardTable aria-label="Computadores">
        <THead>
          <Tr>
            <Th>Nome</Th>
            <Th>Situação</Th>
            <Th>Versão</Th>
            <Th>Atualização</Th>
            <Th>Último contato</Th>
            <Th>Pareado em</Th>
          </Tr>
        </THead>
        <TBody>
          {data.agents.map((agent) => (
            <Tr key={agent.id}>
              <Td heading>{agent.name}</Td>
              <Td label="Situação">
                {agent.status === 'revoked' ? (
                  <>
                    <Badge tone="neutral">Revogado</Badge>{' '}
                    <span className="muted">{revokeReasonLabel(agent.revokeReason)}</span>
                  </>
                ) : agent.online ? (
                  <Badge tone="success" dot>
                    Online
                  </Badge>
                ) : (
                  <Badge tone="error" dot>
                    Offline
                  </Badge>
                )}
              </Td>
              <Td label="Versão">
                <span className="mono">{agent.agentVersion ?? '—'}</span>
                <span className="muted"> · protocolo {agent.protocolVersion ?? '—'}</span>
                {agent.protocolVersion !== null && (
                  <div style={{ fontSize: 12 }}>
                    {agent.protocolSupported ? (
                      <span className="muted">protocolo aceito</span>
                    ) : (
                      <Badge tone="error">protocolo recusado</Badge>
                    )}
                  </div>
                )}
              </Td>
              <Td label="Atualização">
                <AgentUpdateCell agent={agent} />
              </Td>
              <Td label="Último contato">
                {formatSince(agent.lastSeenAt)}
                {agent.lastSeenAt && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {formatWhen(agent.lastSeenAt)}
                  </div>
                )}
              </Td>
              <Td label="Pareado em">{formatWhen(agent.pairedAt)}</Td>
            </Tr>
          ))}
        </TBody>
      </CardTable>
    </div>
  );
}

/**
 * O nível de atualização do computador e o último pedido: versão-alvo, canal congelado,
 * modo, estado, fase, código da falha e horários. Sem quem confirmou no painel, que a API
 * não devolve, e sem nenhuma ação: pedir a atualização é do dono.
 */
function AgentUpdateCell({ agent }: { agent: TenantPrintingAgent }) {
  const request = agent.lastUpdateRequest;
  const levelTone: BadgeTone =
    agent.updateLevel === 'mandatory' ? 'error' : agent.updateLevel === 'recommended' ? 'warning' : 'neutral';

  return (
    <>
      {agent.status === 'revoked' ? (
        <span className="muted">—</span>
      ) : agent.updateLevel === 'none' ? (
        <span className="muted">{updateLevelLabel(agent.updateLevel)}</span>
      ) : (
        <Badge tone={levelTone}>{updateLevelLabel(agent.updateLevel)}</Badge>
      )}
      {request && (
        <div style={{ fontSize: 12, marginTop: 4 }} data-testid={`update-request-${agent.id}`}>
          <div>
            Pedido para <span className="mono">{request.targetVersion}</span> ({channelLabel(request.channel).toLowerCase()}),{' '}
            {updateModeLabel(request.mode)}: <strong>{updateStatusLabel(request.status)}</strong>
          </div>
          {request.phase && request.status !== 'succeeded' && (
            <div className="muted">fase: {updatePhaseLabel(request.phase)}</div>
          )}
          {request.failureCode && (
            <div>
              <span className="mono">{request.failureCode}</span>{' '}
              <span className="muted">— {updateFailureLabel(request.failureCode)}</span>
            </div>
          )}
          <div className="muted">
            confirmado em {formatWhen(request.requestedAt)}
            {request.finishedAt
              ? ` · terminou em ${formatWhen(request.finishedAt)}`
              : request.status === 'requested'
                ? ` · a partir de ${formatWhen(request.notBefore)}`
                : ''}
          </div>
        </div>
      )}
    </>
  );
}

function PrintersTable({ data }: { data: TenantPrintingResponse }) {
  if (data.printers.length === 0) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        Nenhuma impressora cadastrada.
      </p>
    );
  }

  return (
    <div>
      <div className="card-subtitle" style={{ marginBottom: 6 }}>
        Impressoras
      </div>
      <CardTable aria-label="Impressoras">
        <THead>
          <Tr>
            <Th>Nome</Th>
            <Th>Modo</Th>
            <Th>Largura</Th>
            <Th>Estado</Th>
            <Th>Último erro</Th>
          </Tr>
        </THead>
        <TBody>
          {data.printers.map((printer) => (
            <Tr key={printer.id}>
              <Td heading>{printer.displayName}</Td>
              <Td label="Modo">{transportLabel(printer.transport)}</Td>
              <Td label="Largura">{widthLabel(printer.width)}</Td>
              <Td label="Estado">
                <Badge tone={printer.state === 'ok' ? 'success' : printer.state === 'unknown' ? 'neutral' : 'error'}>
                  {printerStateLabel(printer.state)}
                </Badge>
                {!printer.available && <span className="muted"> · sem computador ativo</span>}
              </Td>
              <Td label="Último erro">
                {printer.lastError ? (
                  <>
                    {jobErrorLabel(printer.lastError.code)}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {formatWhen(printer.lastError.at)}
                    </div>
                  </>
                ) : (
                  <span className="muted">—</span>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </CardTable>
    </div>
  );
}

function TicketsList({ data }: { data: TenantPrintingResponse }) {
  if (data.ticketsByPrinter.length === 0) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        Nenhum cupom mapeado para impressora.
      </p>
    );
  }

  return (
    <div>
      <div className="card-subtitle" style={{ marginBottom: 6 }}>
        Cupons por impressora
      </div>
      <ul aria-label="Cupons por impressora" style={{ margin: 0, paddingLeft: 18 }}>
        {data.ticketsByPrinter.map((printer) => (
          <li key={printer.printerId}>
            <strong>{printer.printerName}</strong>:{' '}
            {printer.tickets
              .map((t) => `${ticketLabel(t.kind, t.stationName)} (${t.copies === 1 ? '1 via' : `${t.copies} vias`})`)
              .join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EventsTable({ data }: { data: TenantPrintingResponse }) {
  const agentNames = useMemo(() => new Map(data.agents.map((a) => [a.id, a.name])), [data.agents]);

  if (data.recentEvents.length === 0) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        Nenhum evento do computador.
      </p>
    );
  }

  return (
    <div>
      <div className="card-subtitle" style={{ marginBottom: 6 }}>
        Últimos eventos do computador
      </div>
      <CardTable aria-label="Últimos eventos do computador">
        <THead>
          <Tr>
            <Th>Quando</Th>
            <Th>Evento</Th>
            <Th>Código</Th>
            <Th>Computador</Th>
          </Tr>
        </THead>
        <TBody>
          {data.recentEvents.map((event, index) => (
            <Tr key={`${event.occurredAt}-${index}`}>
              <Td heading>{formatWhen(event.occurredAt)}</Td>
              <Td label="Evento">{eventLabel(event.type)}</Td>
              <Td label="Código">
                {event.code ? <span className="mono">{event.code}</span> : <span className="muted">—</span>}
              </Td>
              <Td label="Computador">
                {(event.agentId && agentNames.get(event.agentId)) ?? <span className="muted">—</span>}
              </Td>
            </Tr>
          ))}
        </TBody>
      </CardTable>
    </div>
  );
}
